<script setup lang="ts">
import type { SelfAssessmentProps } from '@core/types'
import { computed, useDevice, useDefaults, useTheme, useRoute } from '#imports'

const _props = withDefaults(defineProps<SelfAssessmentProps>(), {
    height: '20px',
    color: undefined,
    swapLeftMiddleOnDesktop: false
})

const props = useDefaults({
    props: _props,
    defaults: {
        color: 'background'
    }
})

const route = useRoute()
const { theme } = useTheme(() => ({ theme: props.theme }))
const { isDesktop } = useDevice()
const shouldSwapLayout = computed<boolean>(() => props.swapLeftMiddleOnDesktop && isDesktop)

const colorStyles = computed(() => {
    if (!props.color) return {}

    const bgColor = theme.value.colors[props.color as keyof typeof theme.value.colors]
    const onColorKey = `on-${props.color}`
    const textColor = theme.value.colors[onColorKey as keyof typeof theme.value.colors]

    return {
        '--color': bgColor,
        '--on-color': textColor,
    }
})
</script>

<template>
    <ThemeProvider :theme="props.theme">
        <div
            class="self-assessment"
            :class="{
                'self-assessment--playgame': props.isPlaygame || route.meta.isPlaygame,
                'self-assessment--swapped': shouldSwapLayout
            }"
            :style="{
                ...colorStyles,
                height: props.height
            }"
        >
            <div class="self-assessment__content">
                <div class="self-assessment__left">
                    <slot name="left" />
                </div>

                <div class="self-assessment__middle">
                    <slot name="middle">
                        <template v-if="props.links?.length">
                            <span class="self-assessment__age">18+</span>
                            <NuxtLink
                                v-for="(link, i) in props.links"
                                :key="i"
                                :href="link.href"
                                :external="link.external"
                                target="_blank"
                                class="self-assessment__link"
                            >
                                {{ link.text }}
                            </NuxtLink>
                            <div class="self-assessment__clock">
                                <ClientOnly>
                                    <UIClockCounter :with-icon="false" />
                                </ClientOnly>
                            </div>
                        </template>
                    </slot>
                </div>

                <div class="self-assessment__right">
                    <slot name="right" />
                </div>
            </div>
        </div>
    </ThemeProvider>
</template>

<style lang="scss" scoped>
.self-assessment {
    display: flex;
    z-index: z(self-assessment);
    align-self: center;
    width: 100%;
    color: var(--on-color, var(--self-assessment-color, #{$self-assessment-color}));
    background: var(--color, var(--self-assessment-bg, #{$self-assessment-bg}));
    font-size: $font-size-xxs - 2;
    padding-top: 3px;
    --self-assessment-border: var(--border-surface, #d0d2db);

    @include mobile(sm) {
        font-size: $font-size-xxs - 1;
    }

    @include tablet(md) {
        position: static;
    }

    &__content {
        display: grid;
        grid-template: 'left middle right';
        grid-template-columns: 1fr auto 1fr;
        flex: 1;
        align-items: center;
        max-width: 100%;
        padding: 0 5px;
        color: inherit;

        @include mobile(sm) {
            padding: 0 10px;
        }

        @include tablet(md) {
            padding: 0 30px;
        }
    }

    &__left,
    &__middle,
    &__right {
        display: grid;
        grid-auto-flow: column;
        align-items: center;
        line-height: $font-size-xxs - 2;

        @include mobile(sm) {
            line-height: $font-size-xxs - 1;
        }
    }

    &__left {
        grid-area: left;
    }

    &__middle {
        grid-area: middle;
        justify-content: center;
    }

    &__right {
        grid-area: right;
        justify-content: flex-end;
    }

    // Styles for default rendered content (links prop)
    &__link {
        color: inherit;
        text-decoration: none;
        padding: 0 4px;
        border-right: 1px solid var(--self-assessment-border);
        transition: all 0.3s;

        &:first-of-type {
            border-left: 1px solid var(--self-assessment-border);
        }
    }

    &__age {
        padding: 0 4px;
    }

    &__clock {
        display: flex;
        padding-left: 4px;
        min-width: 32px;
    }

    a {
        text-decoration: none;
        color: inherit;
    }

    :slotted(a) {
        text-decoration: none;
        color: inherit;
    }

    &--swapped {
        .self-assessment__left {
            grid-area: middle;
            justify-content: center;
        }

        .self-assessment__middle {
            grid-area: left;
            justify-content: start;
        }
    }

    &--playgame {
        color: #fff;
        background: #000;
        --self-assessment-border: rgba(255, 255, 255, 0.3);

        .self-assessment__left {
            display: flex;
            align-items: unset;
            grid-auto-flow: unset;
        }
    }
}
</style>
